/**
 * The events a product's Meta dataset receives, and what Meta learns from (notes/AUDIENCE.md 5.6, 8): each
 * shown by its Meta name and a few plain words. Visits are never offered as something to learn from, and
 * a site's own event goes to Meta by its exact name.
 */

/** Meta's standard events, with the words the Ads step shows and Zernio's name for each (docs: customEventType). */
export const STANDARD_EVENTS: Record<string, { words: string; zernio: string; plural: string }> = {
  Schedule: { words: "Someone booked", zernio: "SCHEDULE", plural: "Bookings" },
  Lead: { words: "Someone signed up", zernio: "LEAD", plural: "Sign-ups" },
  CompleteRegistration: { words: "Someone registered", zernio: "COMPLETE_REGISTRATION", plural: "Registrations" },
  Purchase: { words: "Someone paid", zernio: "PURCHASE", plural: "Payments" },
  Contact: { words: "Someone contacted you", zernio: "CONTACT", plural: "Contacts" },
  SubmitApplication: { words: "Someone applied", zernio: "SUBMIT_APPLICATION", plural: "Applications" },
  StartTrial: { words: "Someone started a trial", zernio: "START_TRIAL", plural: "Trials" },
  Subscribe: { words: "Someone subscribed", zernio: "SUBSCRIBE", plural: "Subscriptions" },
  InitiateCheckout: { words: "Someone started to pay", zernio: "INITIATE_CHECKOUT", plural: "Checkouts" },
  AddToCart: { words: "Someone added to the cart", zernio: "ADD_TO_CART", plural: "Carts" },
  ViewContent: { words: "Someone viewed a key page", zernio: "VIEW_CONTENT", plural: "Key pages viewed" },
};

/** Visits: the pixel's base code sends PageView on every page, and ViewContent marks a page seen. Never what Meta learns from. */
export const VISIT_EVENTS = new Set(["PageView", "ViewContent"]);

/** Results that start with someone getting in touch: Meta's leads objective; the rest are sales. */
const LEAD_EVENTS = new Set(["LEAD", "SCHEDULE", "CONTACT", "SUBMIT_APPLICATION", "COMPLETE_REGISTRATION"]);

/** Under about 50 a week, Meta's learning phase does not end (Meta Business Help 112167992830700). */
export const RARE_PER_WEEK = 50;

export const eventWords = (name: string) => STANDARD_EVENTS[name]?.words ?? "Sent by your site";
export const eventPlural = (name: string) => STANDARD_EVENTS[name]?.plural ?? `${name} events`;

export interface Optimise {
  goal: "lead_conversion" | "conversions" | "traffic";
  event: string;
  eventStr?: string;
}

/** What Meta learns from, as the goal's `optimise` setting: a standard event by Zernio's name, a site's own as OTHER with its name. */
export function optimiseFor(event: string): Optimise {
  const std = STANDARD_EVENTS[event];
  if (!std) return { goal: "conversions", event: "OTHER", eventStr: event };
  return { goal: LEAD_EVENTS.has(std.zernio) ? "lead_conversion" : "conversions", event: std.zernio };
}

/** The Meta event name an `optimise` setting stands for. */
export function eventOf(o: Optimise): string {
  if (o.event === "OTHER") return o.eventStr ?? "";
  return Object.entries(STANDARD_EVENTS).find(([, v]) => v.zernio === o.event.toUpperCase())?.[0] ?? o.event;
}

/** One event the dataset received over the week, with its count. */
export interface DatasetEvent {
  name: string;
  count: number;
}

/** Meta's hourly stats rows added up per event, the most frequent first. */
export function sumEvents(rows: { data?: { value?: string; count?: number | string }[] }[]): DatasetEvent[] {
  const by = new Map<string, number>();
  for (const r of rows) for (const d of r.data ?? []) if (d.value) by.set(d.value, (by.get(d.value) ?? 0) + Number(d.count ?? 0));
  return [...by.entries()].map(([name, count]) => ({ name, count })).toSorted((a, b) => b.count - a.count);
}
